import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { useTranslation } from "react-i18next";
import PageTitle from "../../layouts/PageTitle";
import config from "../../../../config.json";
import { getMenuLanguages } from "../../../constants/menuLanguages";
import { itemTitle } from "../Cms/MenuTree/menuTreeUtils";
import { formatDualMoney } from "../../../utils/currency";
import "../../../i18n";
import "./AddCategory.css";

const photoUrl = (photo) => `${config.UPLOADS_SERVER}/product/${photo}`;

const pickLocalized = (details, lang, field, fallback = "") => {
  const list = Array.isArray(details) ? details : [];
  const match = list.find((d) => d?.language === lang && String(d?.[field] || "").trim());
  if (match?.[field]) return match[field];
  const en = list.find((d) => d?.language === "en" && String(d?.[field] || "").trim());
  if (en?.[field]) return en[field];
  const first = list.find((d) => String(d?.[field] || "").trim());
  return first?.[field] || fallback;
};

const ItemDetails = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const languages = useMemo(() => getMenuLanguages(t), [t]);

  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openLangs, setOpenLangs] = useState({});
  const [lightbox, setLightbox] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await axios.get(`${config.dev}/v2/items/retrieve/${id}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("tokenLogin")}` },
        });
        if (cancelled) return;
        setItem(response?.data || {});
        setOpenLangs({});
      } catch (err) {
        console.error("Error fetching item:", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const isLangOpen = (code) => Boolean(openLangs[code]);
  const toggleLang = (code) => setOpenLangs((prev) => ({ ...prev, [code]: !prev[code] }));
  const usedLangs = languages.filter((lang) =>
    (item?.itemDetails || []).some(
      (d) => d.language === lang.code && String(d.itemTitle || "").trim()
    )
  );
  const langsToShow = usedLangs.length ? usedLangs : languages.filter((lang) => lang.code === "en");

  const photoSrc =
    typeof item?.photo === "string" && item.photo.trim() ? photoUrl(item.photo.trim()) : null;
  const fallbackTitle = itemTitle(item, t("itemDetails"));

  if (loading) {
    return (
      <>
        <PageTitle activeMenu={t("itemDetails")} motherMenu={t("kitchen")} />
        <div className="catform">
          <p className="catform__empty">{t("loading")}…</p>
        </div>
      </>
    );
  }

  if (!item?._id) {
    return (
      <>
        <PageTitle activeMenu={t("itemDetails")} motherMenu={t("kitchen")} />
        <div className="catform">
          <p className="catform__empty">{t("noTitle")}</p>
          <div className="catform__actions">
            <button
              type="button"
              className="btn catform__btn catform__btn--cancel"
              onClick={() => navigate("/items")}
            >
              {t("itemsList")}
            </button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <PageTitle activeMenu={t("itemDetails")} motherMenu={t("kitchen")} />

      <div className="catform catform--sheet">
        <div className="catform__top catform__identity">
          <button
            type="button"
            className={`catform__cover${photoSrc ? "" : " is-empty"}`}
            onClick={() => photoSrc && setLightbox(true)}
            aria-label={photoSrc ? t("prodFormPhoto") : t("prodSheetNoPhoto")}
          >
            {photoSrc ? <img src={photoSrc} alt="" /> : <i className="fas fa-camera" aria-hidden />}
          </button>
          <div className="catform__identity-copy">
            <p className="catform__kicker">{t("itemSheetSubtitle")}</p>
            <h1 className="catform__title">{fallbackTitle}</h1>
            <div className="catform__facts">
              <span className="catform__fact">{formatDualMoney(item.price)}</span>
            </div>
          </div>
          <div className="catform__top-actions">
            <button type="button" className="btn catform__link" onClick={() => navigate("/items")}>
              <i className="fas fa-arrow-left" aria-hidden /> {t("itemsList")}
            </button>
            <Link to={`/edit-item/${id}`} className="btn catform__btn catform__btn--edit">
              <i className="fas fa-pen" aria-hidden />
              {t("edit")}
            </Link>
          </div>
        </div>

        <section className="catform__section is-open">
          <h2 className="catform__heading">{t("catSheetLanguages")}</h2>
          <p className="catform__help">{t("sheetLangHint")}</p>
          <div className="catform__pills">
            {langsToShow.map((lang) => (
              <button
                key={lang.code}
                type="button"
                className={`catform__pill is-filled ${isLangOpen(lang.code) ? "is-open" : ""}`}
                aria-pressed={isLangOpen(lang.code)}
                onClick={() => toggleLang(lang.code)}
              >
                <img src={lang.flag} alt="" />
                {lang.label}
                <i className="fas fa-check" aria-hidden />
              </button>
            ))}
          </div>
          <div className="catform__lang-fields">
            {langsToShow
              .filter((lang) => isLangOpen(lang.code))
              .map((lang) => (
                <div
                  className="catform__lang-panel"
                  key={lang.code}
                  dir={lang.code === "ar" ? "rtl" : undefined}
                >
                  <div className="catform__field">
                    <span className="catform__label">
                      <img src={lang.flag} alt="" />
                      {lang.label} — {t("title")}
                    </span>
                    <p className="catform__readonly">
                      {pickLocalized(item.itemDetails, lang.code, "itemTitle", t("noTitle"))}
                    </p>
                  </div>
                </div>
              ))}
          </div>
        </section>

        <section className="catform__section">
          <h2 className="catform__heading">{t("prodFormPrice")}</h2>
          <p className="catform__readonly catform__readonly--price">{formatDualMoney(item.price)}</p>
        </section>

        <div className="catform__actions">
          <button
            type="button"
            className="btn catform__btn catform__btn--cancel"
            onClick={() => navigate("/items")}
          >
            {t("itemsList")}
          </button>
          <Link to={`/edit-item/${id}`} className="btn catform__btn catform__btn--edit">
            <i className="fas fa-pen" aria-hidden />
            {t("edit")}
          </Link>
        </div>
      </div>

      {lightbox && photoSrc && (
        <div className="catform-lightbox" onClick={() => setLightbox(false)} role="dialog" aria-modal="true">
          <button type="button" className="catform-lightbox__close" aria-label={t("cancel")}>
            <i className="fas fa-times" />
          </button>
          <img src={photoSrc} alt="" onClick={(e) => e.stopPropagation()} />
        </div>
      )}
    </>
  );
};

export default ItemDetails;
